import {
  Energy,
  Facility,
  HeatSource,
  RefrigerantSource,
  WaterSource
} from "../../../../../../api/facility";
import { FACILITY_OWNERSHIP, FACILITY_TYPES } from "../../../../../../config/constants";
import { formatDecimalValue } from "../../../../../../utils/utils";
import {
  FacilityInfoRow,
  FacilityLike,
  FacilitySourceTableRow,
  FacilityViewModel
} from "./externalStorageFacility.types";

type OptionItem = { text?: string; value?: string | number };

const EMPTY_VALUE = "—";

export const resolveOptionLabel = (
  options: OptionItem[] | undefined,
  value: string | number | undefined
): string => {
  if (value === undefined || value === null || value === "") {
    return EMPTY_VALUE;
  }

  const match = options?.find(option => String(option.value) === String(value));
  return match?.text ?? String(value);
};

export const getFacilityTypeLabel = (value: string | undefined): string =>
  resolveOptionLabel(FACILITY_TYPES, value);

export const getFacilityOwnershipLabel = (value: string | undefined): string =>
  resolveOptionLabel(FACILITY_OWNERSHIP, value);

type CountryOption = {
  id?: string;
  country?: string;
  value?: string;
  text?: string;
};

export const getCountryName = (
  countries: CountryOption[] | undefined,
  countryId: string | undefined
): string => {
  if (!countryId) {
    return EMPTY_VALUE;
  }

  const match = countries?.find(
    country => String(country.id ?? country.value) === String(countryId)
  );
  return match?.country ?? match?.text ?? countryId;
};

export const buildFacilityInfoRows = (
  facility: FacilityLike,
  countries: CountryOption[] | undefined
): FacilityInfoRow[] => [
  { label: "Facility Name", value: facility?.facilityName || EMPTY_VALUE },
  {
    label: "Facility Ownership",
    value: getFacilityOwnershipLabel(facility?.facilityOwnership)
  },
  { label: "Facility Type", value: getFacilityTypeLabel(facility?.facilityType) },
  { label: "Address", value: facility?.facilityAddress || EMPTY_VALUE },
  { label: "City", value: facility?.facilityCity || EMPTY_VALUE },
  {
    label: "Country",
    value: getCountryName(countries, facility?.facilityCountry)
  }
];

const groupByYear = <T extends { Year: number }>(items: T[] = []): Array<{ year: number; items: T[] }> => {
  const grouped = new Map<number, T[]>();

  items.forEach(item => {
    const yearItems = grouped.get(item.Year) ?? [];
    yearItems.push(item);
    grouped.set(item.Year, yearItems);
  });

  return Array.from(grouped.entries()).map(([year, yearItems]) => ({ year, items: yearItems }));
};

const toGroupedTableRows = (
  groups: Array<{ year: number; items: Array<{ id?: string }> }>,
  mapItem: (item: any) => string[]
): FacilitySourceTableRow[] =>
  groups.flatMap(group =>
    group.items.map((item, index) => ({
      id: String(item.id ?? `${group.year}-${index}`),
      year: group.year,
      yearRowSpan: index === 0 ? group.items.length : 0,
      cells: mapItem(item)
    }))
  );

export const mapEnergyTableRows = (
  energy: Energy[] | undefined,
  energyTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  toGroupedTableRows(groupByYear(energy ?? []), item => {
    const kWhValue = Number(item.kWhConsumed) || 0;
    const price = Number(item.Price) || 0;

    return [
      resolveOptionLabel(energyTypes, item.EnergyType),
      `${Number.isNaN(kWhValue) ? 0 : kWhValue} kWh`,
      `${Number.isNaN(Number(item.Percentage)) ? 0 : item.Percentage ?? 0} %`,
      formatDecimalValue(item.Price),
      formatDecimalValue(kWhValue * price)
    ];
  });

export const mapHeatTableRows = (
  heatSources: HeatSource[] | undefined,
  heatTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  toGroupedTableRows(groupByYear(heatSources ?? []), item => {
    const volume = Number(item.Volumes) || 0;
    const price = Number(item.Price) || 0;

    return [
      resolveOptionLabel(heatTypes, item.HeatSource),
      `${Number.isNaN(volume) ? 0 : volume}${item.UnitType ?? ""}`,
      formatDecimalValue(item.Price),
      formatDecimalValue(volume * price)
    ];
  });

export const mapWaterTableRows = (
  waterSources: WaterSource[] | undefined,
  waterTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  toGroupedTableRows(groupByYear(waterSources ?? []), item => {
    const volume = Number(item.Volumes) || 0;
    const price = Number(item.Price) || 0;

    return [
      resolveOptionLabel(waterTypes, item.WaterSource),
      `${Number.isNaN(volume) ? 0 : volume}`,
      formatDecimalValue(item.Price),
      formatDecimalValue(volume * price)
    ];
  });

export const mapRefrigerantTableRows = (
  refrigerantSources: RefrigerantSource[] | undefined,
  refrigerantTypes: OptionItem[] | undefined
): FacilitySourceTableRow[] =>
  toGroupedTableRows(groupByYear(refrigerantSources ?? []), item => {
    const volume = Number(item.Volumes) || 0;
    const price = Number(item.Price) || 0;

    return [
      resolveOptionLabel(refrigerantTypes, item.RefrigerantSource),
      `${Number.isNaN(volume) ? 0 : volume}${item.UnitType ?? ""}`,
      formatDecimalValue(item.Price),
      formatDecimalValue(volume * price)
    ];
  });

export const buildFacilityViewModel = ({
  facility,
  originalFacility,
  countries,
  energyTypes,
  heatTypes,
  waterTypes,
  refrigerantTypes,
  showOriginalPanel
}: {
  facility: Facility | undefined;
  originalFacility: Facility | undefined;
  countries: CountryOption[] | undefined;
  energyTypes: OptionItem[] | undefined;
  heatTypes: OptionItem[] | undefined;
  waterTypes: OptionItem[] | undefined;
  refrigerantTypes: OptionItem[] | undefined;
  showOriginalPanel: boolean;
}): FacilityViewModel | null => {
  if (!facility) {
    return null;
  }

  return {
    title: facility.facilityName || "Facility",
    subtitle: showOriginalPanel ? "Simulated facility" : undefined,
    infoRows: buildFacilityInfoRows(facility, countries),
    originalInfoRows: showOriginalPanel ? buildFacilityInfoRows(originalFacility, countries) : [],
    energyRows: mapEnergyTableRows(facility.energy, energyTypes),
    heatRows: mapHeatTableRows(facility.heatSource, heatTypes),
    waterRows: mapWaterTableRows(facility.waterSource, waterTypes),
    refrigerantRows: mapRefrigerantTableRows(facility.refrigerantSource, refrigerantTypes),
    showOriginalPanel
  };
};
